import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => ({ 'Authorization': `Bearer ${localStorage.getItem('adminToken')}`, 'Content-Type': 'application/json' });
const authOnly = () => ({ 'Authorization': `Bearer ${localStorage.getItem('adminToken')}` });

const scoreColor = (s) => s >= 75 ? 'text-green-600' : s >= 45 ? 'text-amber-600' : 'text-red-500';

const AutoVideoStudio = () => {
  const [projects, setProjects] = useState([]);
  const [options, setOptions] = useState({ color_presets: [], music_tracks: [], event_types: [] });
  const [selected, setSelected] = useState(null); // project id
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  const fetchProjects = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/video-studio/projects`, { headers: authOnly() });
      if (res.ok) setProjects(await res.json());
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    fetchProjects();
    fetch(`${API}/api/admin/video-studio/options`, { headers: authOnly() }).then(r => r.ok && r.json().then(setOptions));
  }, [fetchProjects]);

  if (selected) {
    return <ProjectDetail projectId={selected} options={options} onBack={() => { setSelected(null); fetchProjects(); }} />;
  }

  return (
    <div className="max-w-6xl" data-testid="auto-video-studio">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">🎬 Auto Video Studio</h1>
          <p className="text-gray-500 text-sm mt-1">Upload clips, let AI pick the best moments, and auto-build Reels, TikToks & Stories.</p>
        </div>
        <button onClick={() => setShowCreate(true)} data-testid="new-project-btn"
          className="px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium">+ New Project</button>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" /></div>
      ) : projects.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-xl border border-dashed border-gray-200" data-testid="studio-empty">
          <div className="text-5xl mb-3">🎬</div>
          <h3 className="text-lg font-semibold text-gray-800">No video projects yet</h3>
          <p className="text-gray-500 text-sm mt-1 mb-4">Create a project, upload your GoPro clips, and let AI build the edit.</p>
          <button onClick={() => setShowCreate(true)} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium">+ New Project</button>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="project-list">
          {projects.map(p => (
            <motion.button key={p.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
              onClick={() => setSelected(p.id)} data-testid={`project-card-${p.id}`}
              className="text-left bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-400">{p.event_type}</span>
                <StatusPill status={p.status} />
              </div>
              <h3 className="font-semibold text-gray-900 mt-1">{p.name}</h3>
              <p className="text-xs text-gray-400 mt-2">{p.clip_count} clip{p.clip_count === 1 ? '' : 's'} · {p.color_preset} look</p>
            </motion.button>
          ))}
        </div>
      )}

      {showCreate && <CreateProjectModal options={options} onClose={() => setShowCreate(false)}
        onCreated={(id) => { setShowCreate(false); fetchProjects(); setSelected(id); }} />}
    </div>
  );
};

const StatusPill = ({ status }) => {
  const map = {
    draft: 'bg-gray-100 text-gray-600', rendering: 'bg-purple-100 text-purple-700',
    ready: 'bg-green-100 text-green-700', failed: 'bg-red-100 text-red-700',
  };
  const label = { draft: 'Draft', rendering: 'Rendering…', ready: 'Ready', failed: 'Failed' }[status] || status;
  return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${map[status] || 'bg-gray-100'}`} data-testid="project-status">{label}</span>;
};

const CreateProjectModal = ({ options, onClose, onCreated }) => {
  const [form, setForm] = useState({ name: '', event_type: 'Dive Trip', color_preset: 'scuba', music_track: 'upbeat' });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const create = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/admin/video-studio/projects`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(form) });
      if (res.ok) { const d = await res.json(); onCreated(d.id); }
    } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="create-project-modal">
      <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} className="bg-white rounded-2xl w-full max-w-md shadow-2xl p-6">
        <h2 className="text-lg font-bold text-gray-900 mb-4">New Video Project</h2>
        <div className="space-y-4">
          <Field label="Project Name">
            <input value={form.name} onChange={e => set('name', e.target.value)} data-testid="project-name" placeholder="e.g. Shelly Beach Dive — June"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Event">
              <select value={form.event_type} onChange={e => set('event_type', e.target.value)} data-testid="project-event" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white">
                {options.event_types.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </Field>
            <Field label="Color Look">
              <select value={form.color_preset} onChange={e => set('color_preset', e.target.value)} data-testid="project-color" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white">
                {options.color_presets.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Music">
            <select value={form.music_track} onChange={e => set('music_track', e.target.value)} data-testid="project-music" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white">
              {options.music_tracks.map(m => <option key={m.value} value={m.value}>{m.label}{m.bpm ? ` · ${m.bpm} BPM` : ''}</option>)}
            </select>
          </Field>
        </div>
        <div className="flex justify-end gap-3 mt-5">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900">Cancel</button>
          <button onClick={create} disabled={saving} data-testid="create-project-submit" className="px-5 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white rounded-lg text-sm font-medium">{saving ? 'Creating…' : 'Create'}</button>
        </div>
      </motion.div>
    </div>
  );
};

const Field = ({ label, children }) => (
  <div><label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>{children}</div>
);

const ProjectDetail = ({ projectId, options, onBack }) => {
  const [project, setProject] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadMsg, setUploadMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    const res = await fetch(`${API}/api/admin/video-studio/projects/${projectId}`, { headers: authOnly() });
    if (res.ok) setProject(await res.json());
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (project?.status === 'rendering' && !pollRef.current) {
      pollRef.current = setInterval(load, 4000);
    } else if (project?.status !== 'rendering' && pollRef.current) {
      clearInterval(pollRef.current); pollRef.current = null;
    }
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [project?.status, load]);

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    for (let i = 0; i < files.length; i++) {
      setUploadMsg(`Analyzing ${i + 1}/${files.length}: ${files[i].name}…`);
      const fd = new FormData();
      fd.append('file', files[i]);
      try {
        await fetch(`${API}/api/admin/video-studio/projects/${projectId}/clips`, { method: 'POST', headers: authOnly(), body: fd });
      } catch (err) { /* continue */ }
      await load();
    }
    setUploading(false); setUploadMsg('');
    if (fileRef.current) fileRef.current.value = '';
  };

  const toggleClip = async (clip) => {
    await fetch(`${API}/api/admin/video-studio/clips/${clip.id}`, { method: 'PUT', headers: authHeaders(), body: JSON.stringify({ included: !clip.included }) });
    load();
  };

  const generate = async () => {
    setBusy(true);
    try {
      await fetch(`${API}/api/admin/video-studio/projects/${projectId}/generate`, { method: 'POST', headers: authHeaders() });
      await load();
    } finally { setBusy(false); }
  };

  const sendToCampaign = async (version, output) => {
    const media_url = `${API}${output.url}`;
    const res = await fetch(`${API}/api/admin/campaigns`, {
      method: 'POST', headers: authHeaders(),
      body: JSON.stringify({
        title: `${project.name} — ${version[0].toUpperCase() + version.slice(1)}`,
        content_description: `${project.event_type} auto-edited ${version}`,
        media_type: 'video', media_url, media_source: 'upload', platforms: ['instagram'], status: 'draft',
      }),
    });
    if (res.ok) alert(`Sent "${version}" to Media Campaigns as a draft. Open Media Campaigns to add captions and post.`);
  };

  if (!project) return <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" /></div>;

  const clips = project.clips || [];
  const includedCount = clips.filter(c => c.included).length;

  return (
    <div className="max-w-6xl" data-testid="project-detail">
      <button onClick={onBack} data-testid="back-btn" className="text-sm text-blue-600 hover:underline mb-3">← All projects</button>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
        <div>
          <div className="flex items-center gap-2"><h1 className="text-2xl font-bold text-gray-900">{project.name}</h1><StatusPill status={project.status} /></div>
          <p className="text-gray-500 text-sm mt-1">{project.event_type} · {project.color_preset} look · {options.music_tracks.find(m => m.value === project.music_track)?.label || project.music_track}</p>
        </div>
        <div className="flex gap-3">
          <input ref={fileRef} type="file" accept="video/mp4,video/quicktime,video/webm" multiple onChange={handleFiles} className="hidden" data-testid="clip-file-input" />
          <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="upload-clips-btn"
            className="px-4 py-2 border border-gray-300 hover:bg-gray-50 disabled:opacity-60 text-gray-700 rounded-lg text-sm font-medium">
            {uploading ? 'Uploading…' : '⬆️ Upload clips'}
          </button>
          <button onClick={generate} disabled={busy || includedCount === 0 || project.status === 'rendering'} data-testid="generate-video-btn"
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium">
            {project.status === 'rendering' ? 'Rendering…' : '✨ Generate Videos'}
          </button>
        </div>
      </div>

      {uploadMsg && <div className="mb-4 p-2.5 bg-indigo-50 text-indigo-700 rounded-lg text-sm inline-flex items-center gap-2"><span className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600" />{uploadMsg}</div>}
      {project.status === 'failed' && project.error && <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm" data-testid="render-error">Render failed: {project.error}</div>}

      {/* Outputs */}
      {project.status === 'ready' && project.outputs && Object.keys(project.outputs).length > 0 && (
        <div className="mb-6" data-testid="outputs">
          <h2 className="font-semibold text-gray-900 mb-3">✅ Your videos are ready</h2>
          <div className="grid gap-4 sm:grid-cols-3">
            {['reel', 'tiktok', 'story'].filter(v => project.outputs[v]).map(v => (
              <div key={v} className="bg-white rounded-xl border border-gray-200 p-3" data-testid={`output-${v}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-semibold text-gray-800 capitalize">{v}</span>
                  <span className="text-xs text-gray-400">{project.outputs[v].duration}s</span>
                </div>
                <video src={`${API}${project.outputs[v].url}`} controls className="w-full rounded-lg bg-black aspect-[9/16] object-cover" data-testid={`video-${v}`} />
                <button onClick={() => sendToCampaign(v, project.outputs[v])} data-testid={`send-campaign-${v}`}
                  className="w-full mt-2 px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-xs font-medium">Send to Campaigns →</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Clips */}
      <h2 className="font-semibold text-gray-900 mb-1">Clips <span className="text-sm font-normal text-gray-400">({includedCount} of {clips.length} included)</span></h2>
      <p className="text-xs text-gray-400 mb-3">AI scores each clip; low-scoring clips are auto-excluded. Toggle to override.</p>
      {clips.length === 0 ? (
        <div className="text-center py-14 bg-white rounded-xl border border-dashed border-gray-200 text-sm text-gray-500">Upload clips to get started. MP4/MOV, up to ~30s each.</div>
      ) : (
        <div className="grid gap-3 grid-cols-2 sm:grid-cols-3 lg:grid-cols-4" data-testid="clips-grid">
          {clips.map(c => (
            <div key={c.id} className={`bg-white rounded-xl border p-2 transition-all ${c.included ? 'border-blue-300 ring-1 ring-blue-200' : 'border-gray-200 opacity-60'}`} data-testid={`clip-${c.id}`}>
              <div className="relative">
                {c.thumbnail_url ? (
                  <img src={`${API}${c.thumbnail_url}`} alt="" className="w-full aspect-video object-cover rounded-lg bg-gray-100" />
                ) : (<div className="w-full aspect-video rounded-lg bg-gray-100 flex items-center justify-center text-2xl">🎬</div>)}
                <span className={`absolute top-1 left-1 px-1.5 py-0.5 rounded bg-black/70 text-xs font-bold ${scoreColor(c.score)}`}>{c.score}</span>
                {c.hq_storage_path ? <span className="absolute top-1 right-1 px-1.5 py-0.5 rounded bg-emerald-600 text-white text-[10px] font-bold" data-testid={`hq-badge-${c.id}`}>4K HQ</span> : null}
              </div>
              <div className="mt-1.5">
                <div className="text-xs font-medium text-gray-700 truncate">{c.original_filename}</div>
                <div className="text-[10px] text-gray-400">{c.duration}s · {c.role}{c.tags?.length ? ` · ${c.tags.slice(0, 2).join(', ')}` : ''}</div>
                <label className="flex items-center gap-1.5 mt-1.5 cursor-pointer">
                  <input type="checkbox" checked={!!c.included} onChange={() => toggleClip(c)} data-testid={`include-${c.id}`} className="rounded" />
                  <span className="text-xs text-gray-600">Include</span>
                </label>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AutoVideoStudio;
